import { sortBy } from 'lodash-es'
import type { DateTime } from 'luxon'

import type { Day, SeriesTimeInfo } from './types'

// By default, include the day/time if it occurs at least this many times
const DEFAULT_THRESHOLD_TO_INCLUDE = 2

function getTimeInfo(sessionStartDates: DateTime[]): SeriesTimeInfo[] {
  const occurrencesByTimeInfoKey: Record<string, SeriesTimeInfo> = {}

  sessionStartDates.forEach(startDate => {
    const dayIndex = startDate.weekday
    const time = startDate.toFormat('t')
    const dayLabel = startDate.toFormat('cccc') as Day
    const timezone = startDate.toFormat('ZZZZ')

    const timeInfoKey = `${dayIndex}:${dayLabel}:${time}`

    const existing = occurrencesByTimeInfoKey[timeInfoKey]
    if (existing == null) {
      occurrencesByTimeInfoKey[timeInfoKey] = {
        dayIndex,
        dayLabel,
        time,
        numOccurrences: 1,
        timezone,
      }
    } else {
      existing.numOccurrences += 1
    }
  })

  return Object.values(occurrencesByTimeInfoKey)
}

export default function getSeriesTimeInfo({
  sessionStartDates,
  thresholdToInclude = DEFAULT_THRESHOLD_TO_INCLUDE,
}: {
  sessionStartDates: DateTime[]
  thresholdToInclude?: number
}): SeriesTimeInfo[] {
  if (sessionStartDates.length === 0) {
    return []
  }

  const timeInfos: SeriesTimeInfo[] = []
  const allTimeInfos: SeriesTimeInfo[] = getTimeInfo(sessionStartDates)

  if (allTimeInfos.length === 1) {
    return allTimeInfos
  }

  allTimeInfos.forEach(seriesTimeInfo => {
    if (seriesTimeInfo.numOccurrences >= thresholdToInclude) {
      timeInfos.push(seriesTimeInfo)
    }
  })

  const firstDayIndex = sessionStartDates[0].weekday

  return sortBy(timeInfos, timeInfo => (timeInfo.dayIndex - firstDayIndex) % 7)
}
